import { useState } from "react";
import { motion } from "framer-motion";
import { Calculator, Loader2, DollarSign, Users, MapPin, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }) };

const EVENT_TYPES = ["Wedding", "Corporate Gala", "Yacht Party", "Birthday", "Product Launch", "Concert", "Festival", "Charity Event"];
const SERVICES = ["Photography", "Videography", "Drone Coverage", "Live Streaming", "DJ/Entertainment", "Decor", "Catering", "Coordination"];

export default function BudgetCalculatorPage() {
  const [eventType, setEventType] = useState("");
  const [guests, setGuests] = useState(50);
  const [location, setLocation] = useState("Jamaica");
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const toggleService = (s) => setServices(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  const calculate = async () => {
    if (!eventType) { toast.error("Select an event type."); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai/budget-calculator`, { event_type: eventType, guest_count: guests, location, services, budget_preference: "premium" }, { timeout: 60000 });
      setResult(res.data.budget);
    } catch { toast.error("Calculation failed."); } finally { setLoading(false); }
  };

  return (
    <div data-testid="budget-calculator-page" className="pt-20 min-h-screen bg-luxury-black">
      <section className="py-24 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" animate="visible" className="text-center mb-12">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.3em] text-gold mb-4">AI Budget Planner</motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-4xl sm:text-5xl font-light text-white mb-4">
              Event Budget <span className="gold-gradient-text">Calculator</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-white/50 font-body text-base max-w-lg mx-auto">
              Get an AI-generated detailed cost breakdown for your dream event.
            </motion.p>
          </motion.div>

          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={3} className="glass-card p-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-white/50 text-xs font-body uppercase tracking-wider mb-2 block">Event Type *</label>
                <select data-testid="budget-event-type" value={eventType} onChange={e => setEventType(e.target.value)} className="w-full bg-white/5 border border-white/10 text-white text-sm px-4 py-3 rounded-sm focus:outline-none focus:border-gold/50 font-body">
                  <option value="" className="bg-black">Select type...</option>
                  {EVENT_TYPES.map(t => <option key={t} value={t} className="bg-black">{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-white/50 text-xs font-body uppercase tracking-wider mb-2 block flex items-center gap-2"><Users size={12} /> Guest Count</label>
                <Input data-testid="budget-guests" type="number" value={guests} onChange={e => setGuests(parseInt(e.target.value) || 50)} className="bg-white/5 border-white/10 text-white h-12 rounded-sm" />
              </div>
              <div>
                <label className="text-white/50 text-xs font-body uppercase tracking-wider mb-2 block flex items-center gap-2"><MapPin size={12} /> Location</label>
                <Input data-testid="budget-location" value={location} onChange={e => setLocation(e.target.value)} className="bg-white/5 border-white/10 text-white h-12 rounded-sm" />
              </div>
            </div>
            <div>
              <label className="text-white/50 text-xs font-body uppercase tracking-wider mb-3 block">Services Needed</label>
              <div className="flex flex-wrap gap-2">
                {SERVICES.map(s => (
                  <button key={s} onClick={() => toggleService(s)} data-testid={`budget-svc-${s.toLowerCase().replace(/\//g, "-")}`} className={`px-4 py-2 text-xs font-body transition-all border ${services.includes(s) ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-white/40 hover:border-white/20"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <button onClick={calculate} disabled={loading} data-testid="budget-calculate-btn" className="w-full flex items-center justify-center gap-2 bg-gold text-black font-body font-medium text-sm py-4 hover:bg-gold-light transition-colors disabled:opacity-50">
              {loading ? <><Loader2 size={16} className="animate-spin" /> Calculating...</> : <><Calculator size={16} /> Calculate Budget</>}
            </button>
          </motion.div>

          {result && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 glass-card p-8" data-testid="budget-result">
              <div className="flex items-center gap-2 mb-6"><Sparkles size={18} className="text-gold" /><h3 className="text-white font-heading text-lg">AI Budget Breakdown</h3></div>
              <div className="text-white/70 font-body text-sm leading-relaxed whitespace-pre-wrap">{result}</div>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
}
